import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, mergeAttributes, Extension } from '@tiptap/core';
import { NodeSelection } from 'prosemirror-state';
import StarterKit from '@tiptap/starter-kit';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import katex from 'katex';
import { FiTrash2, FiBold, FiItalic, FiUnderline } from 'react-icons/fi';
import { MdFormatAlignLeft, MdFormatAlignCenter, MdFormatAlignRight } from 'react-icons/md';
import 'katex/dist/katex.min.css';
import TextEditorToolsBar from '../../ui/textEditorElements/textEditorToolsBar';
import { useColorContext } from '../../../context/colorContext';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const escapeHtmlAttr = (value = '') => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const stripAlignedEnvironment = (latex = '') => {
  const source = String(latex || '').trim();
  if (!source) return '';

  // Treat \begin{aligned}...\end{aligned} as transparent wrappers for editor rendering.
  const singleLineWrapped = /^\\begin\s*\{aligned\}\s*([\s\S]*?)\s*\\end\s*\{aligned\}$/;
  const match = source.match(singleLineWrapped);
  if (match) {
    return String(match[1] || '').trim();
  }

  return source;
};

const shouldRenderAsDisplayMath = (latex = '') => {
  const value = stripAlignedEnvironment(latex);
  if (!value) return false;
  return /\\begin\s*\{aligned\}|\\end\s*\{aligned\}/.test(value);
};

const normalizeLatexContent = (content = '') => {
  if (!content || typeof content !== 'string') return '';

  // Convertit $$...$$ puis $...$ en noeuds math TipTap pour rendu KaTeX.
  const blockConverted = content.replace(/\$\$([\s\S]*?)\$\$/g, (_, expr) => {
    const latex = stripAlignedEnvironment((expr || '').trim());
    if (!latex) return '$$ $$';
    return `<span data-type="math-inline" data-display="true" data-latex="${escapeHtmlAttr(latex)}"></span>`;
  });

  return blockConverted.replace(/\$([^$\n]+)\$/g, (_, expr) => {
    const latex = stripAlignedEnvironment((expr || '').trim());
    if (!latex) return '$$';
    const displayAttr = shouldRenderAsDisplayMath(latex) ? ' data-display="true"' : '';
    return `<span data-type="math-inline"${displayAttr} data-latex="${escapeHtmlAttr(latex)}"></span>`;
  });
};

const buildAlignedEqualsLatex = (latex = '', minCharsBeforeSplit = 64) => {
  const source = String(latex || '').trim();
  if (!source || !source.includes('=')) {
    return { latex: source, usedAligned: false };
  }

  const rawParts = source.split('=');
  if (rawParts.length < 2) {
    return { latex: source, usedAligned: false };
  }

  const shouldSplit = rawParts.length > 2 || source.length >= minCharsBeforeSplit;
  if (!shouldSplit) {
    return { latex: source, usedAligned: false };
  }

  const parts = rawParts.map((part) => {
    const trimmed = String(part || '').trim();
    return trimmed || '\\text{ }';
  });

  const lines = [];
  lines.push(`${parts[0]} &= ${parts[1]}`);
  for (let i = 2; i < parts.length; i += 1) {
    lines.push(`&= ${parts[i]}`);
  }

  return {
    latex: `\\begin{aligned}${lines.join(' \\\\ ')}\\end{aligned}`,
    usedAligned: lines.length > 1,
  };
};

const preserveSpacesInLatex = (latex = '') => {
  const source = String(latex || '');
  if (!source) return source;

  let result = '';
  let i = 0;

  while (i < source.length) {
    // If we're at \text, skip it and preserve spaces inside the braces
    if (source.substr(i, 5) === '\\text' && source[i + 5] === '{') {
      result += '\\text{';
      i += 6;
      let braceCount = 1;

      while (i < source.length && braceCount > 0) {
        const ch = source[i];
        if (ch === '{') braceCount += 1;
        if (ch === '}') braceCount -= 1;
        result += ch;
        i += 1;
      }
      continue;
    }

    // Outside \text, convert spaces to backslash-space
    if (source[i] === ' ') {
      // Skip consecutive spaces and add one escaped space
      while (i < source.length && source[i] === ' ') i += 1;
      result += '\\ ';
      continue;
    }

    result += source[i];
    i += 1;
  }

  return result;
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const escapeHtmlText = (value = '') => escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const EMPTY_TIPTAP_DOC = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

const BACKEND_PUBLIC_BASE = String(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001')
  .trim()
  .replace(/\/+$/g, '');

const normalizeImageSrcForBrowser = (src) => {
  const raw = String(src || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || /^data:image\//i.test(raw)) return raw;

  const fileName = raw.split(/[\\/]/).pop();
  if (!fileName) return raw;

  if (/^[a-zA-Z]:\//.test(raw) || raw.startsWith('\\\\') || /^\/cache\/minneru-images\//i.test(raw) || /\.(png|jpe?g|gif|webp|svg|bmp)(\?.*)?$/i.test(raw)) {
    return `${BACKEND_PUBLIC_BASE}/cache/minneru-images/${encodeURIComponent(fileName)}`;
  }

  return raw;
};

const normalizeTipTapDocImages = (node) => {
  if (!node || typeof node !== 'object') return node;

  const next = { ...node };
  if (next.type === 'image' && next.attrs && typeof next.attrs === 'object') {
    next.attrs = {
      ...next.attrs,
      src: normalizeImageSrcForBrowser(next.attrs.src),
    };
  }

  if (Array.isArray(next.content)) {
    next.content = next.content.map((child) => normalizeTipTapDocImages(child));
  }

  return next;
};

const isTipTapDoc = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (value.type !== 'doc') return false;
  return Array.isArray(value.content);
};

const normalizeLooseMarkdownStructure = (value = '') => {
  const source = String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();

  if (!source) return '';

  // Certains OCR renvoient tout sur une ligne: on force des retours avant les titres markdown.
  // Utilise un lookahead négatif pour ne pas insérer de retour si le # est déjà en début de ligne.
  return source
    .replace(/([^\n])(#{1,6} )/g, '$1\n$2')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const markdownToHtml = (markdown = '') => {
  const source = normalizeLooseMarkdownStructure(markdown);
  if (!source) return '';

  const lines = source.split('\n');
  const parts = [];
  let inUnorderedList = false;
  let inOrderedList = false;

  const closeLists = () => {
    if (inUnorderedList) {
      parts.push('</ul>');
      inUnorderedList = false;
    }
    if (inOrderedList) {
      parts.push('</ol>');
      inOrderedList = false;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      closeLists();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      closeLists();
      const level = headingMatch[1].length;
      const headingRaw = String(headingMatch[2] || '').trim();
      const headingLabelMatch = headingRaw.match(/^(Definition|Theorem|Example|Property|Remark|Corollary|Proof|Attention)\b[:\s-]*(.*)$/i);

      if (headingLabelMatch) {
        const titleOnly = headingLabelMatch[1];
        const remainder = String(headingLabelMatch[2] || '').trim();
        parts.push(`<h${level}>${escapeHtml(titleOnly)}</h${level}>`);
        if (remainder) parts.push(`<p>${escapeHtml(remainder)}</p>`);
      } else {
        const text = escapeHtml(headingRaw);
        parts.push(`<h${level}>${text}</h${level}>`);
      }
      continue;
    }

    const unorderedListMatch = line.match(/^[-*+•]\s+(.+)$/);
    if (unorderedListMatch) {
      if (inOrderedList) {
        parts.push('</ol>');
        inOrderedList = false;
      }
      if (!inUnorderedList) {
        parts.push('<ul>');
        inUnorderedList = true;
      }
      parts.push(`<li>${escapeHtml(unorderedListMatch[1])}</li>`);
      continue;
    }

    const orderedListMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (orderedListMatch) {
      if (inUnorderedList) {
        parts.push('</ul>');
        inUnorderedList = false;
      }
      if (!inOrderedList) {
        parts.push('<ol>');
        inOrderedList = true;
      }
      parts.push(`<li>${escapeHtml(orderedListMatch[2])}</li>`);
      continue;
    }

    const imageMatch = line.match(/^!\[(.*?)\]\((.+?)\)$/);
    if (imageMatch) {
      closeLists();
      const alt = escapeHtmlText(imageMatch[1] || 'image');
      const src = escapeHtmlText(imageMatch[2] || '');
      if (src) parts.push(`<p><img src="${src}" alt="${alt}" /></p>`);
      continue;
    }

    // Si la ligne contient du HTML (ex: <img> intégré dans le markdown), on le passe tel quel.
    if (/<[a-z][^>]*>/i.test(line)) {
      closeLists();
      parts.push(line.startsWith('<') ? line : `<p>${line}</p>`);
      continue;
    }

    closeLists();
    parts.push(`<p>${escapeHtml(line)}</p>`);
  }

  closeLists();
  return parts.join('');
};

const normalizeEditorIncomingContent = (value = EMPTY_TIPTAP_DOC) => {
  if (isTipTapDoc(value)) return normalizeTipTapDocImages(value);

  const raw = typeof value === 'string' ? String(value || '').trim() : '';
  if (!raw) return EMPTY_TIPTAP_DOC;

  // Compatibilite: support string HTML/Markdown en fallback, mais la voie principale est JSON TipTap.
  const looksLikeHtml = /<(h[1-6]|p|ul|ol|li|div|blockquote|pre|code|table|thead|tbody|tr|td|th)[\s>]/i.test(raw);
  const html = looksLikeHtml ? raw : markdownToHtml(raw);
  return normalizeLatexContent(html);
};

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const areContentsEquivalent = (a, b) => {
  if (a === b) return true;
  return safeJsonStringify(a) === safeJsonStringify(b);
};

const serializeNodeToClipboardText = (node) => {
  if (!node) return '';

  if (node.type?.name === 'mathInline') {
    const latex = stripAlignedEnvironment(String(node.attrs?.latex || '')).trim();
    return latex ? `$${latex}$` : '$$';
  }

  if (node.type?.name === 'hardBreak') return '\n';
  if (node.isText) return String(node.text || '');

  if (!node.content || node.content.childCount === 0) return '';

  let result = '';
  node.content.forEach((child, _offset, index) => {
    result += serializeNodeToClipboardText(child);
    if (child.isBlock && index < node.content.childCount - 1) {
      result += '\n';
    }
  });

  return result;
};

const serializeFragmentToClipboardText = (fragment) => {
  if (!fragment || fragment.childCount === 0) return '';

  let result = '';
  fragment.forEach((node, _offset, index) => {
    result += serializeNodeToClipboardText(node);
    if (node.isBlock && index < fragment.childCount - 1) {
      result += '\n';
    }
  });

  return result;
};

const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
        },
    };
  },
});

const FontFamily = Extension.create({
  name: 'fontFamily',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily || null,
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) return {};
              return { style: `font-family: ${attributes.fontFamily}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontFamily:
        (fontFamily) =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontFamily }).run();
        },
      unsetFontFamily:
        () =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle().run();
        },
    };
  },
});

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-width') || element.style.width || element.getAttribute('width') || null,
        renderHTML: (attributes) => (attributes.width ? { 'data-width': attributes.width } : {}),
      },
      align: {
        default: 'center',
        parseHTML: (element) => element.getAttribute('data-align') || 'center',
        renderHTML: (attributes) => ({ 'data-align': attributes.align || 'center' }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const { width, align = 'center', style: existingStyle, ...attrs } = HTMLAttributes;
    const styleParts = [];
    if (existingStyle) styleParts.push(existingStyle);
    styleParts.push('max-width: 100%');
    styleParts.push('height: auto');
    styleParts.push('display: block');
    if (width) styleParts.push(`width: ${width}`);

    if (align === 'left') styleParts.push('margin: 8px auto 8px 0');
    else if (align === 'right') styleParts.push('margin: 8px 0 8px auto');
    else styleParts.push('margin: 8px auto');

    return ['img', mergeAttributes(this.options.HTMLAttributes, attrs, {
      'data-width': width || null,
      'data-align': align,
      style: styleParts.join('; '),
    })];
  },
});

const MathInline = Node.create({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      latex: {
        default: '',
        parseHTML: (element) => stripAlignedEnvironment(element.getAttribute('data-latex') || ''),
        renderHTML: (attributes) => ({
          'data-latex': stripAlignedEnvironment(attributes.latex || ''),
        }),
      },
      display: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-display') === 'true',
        renderHTML: (attributes) => ({
          'data-display': attributes.display ? 'true' : 'false',
        }),
      },
      confidence_score: {
        default: null,
        parseHTML: (element) => {
          const score = element.getAttribute('data-confidence-score');
          return score ? parseFloat(score) : null;
        },
        renderHTML: (attributes) => {
          const score = attributes.confidence_score;
          return score !== null && score !== undefined
            ? { 'data-confidence-score': String(score) }
            : {};
        },
      },
      fontSize: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute('data-font-size');
          const parsed = value ? parseFloat(value) : NaN;
          return Number.isFinite(parsed) ? parsed : null;
        },
        renderHTML: (attributes) => {
          const parsed = attributes?.fontSize !== null && attributes?.fontSize !== undefined
            ? parseFloat(String(attributes.fontSize))
            : NaN;
          return Number.isFinite(parsed)
            ? { 'data-font-size': String(parsed) }
            : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="math-inline"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const latex = HTMLAttributes?.['data-latex'] || '';
    const display = String(HTMLAttributes?.['data-display'] || 'false') === 'true';
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'math-inline',
        class: `math-inline-node${display ? ' math-display-node' : ''}`,
      }),
      `$${latex}$`,
    ];
  },

  addCommands() {
    return {
      insertMathInline:
        (latex, options = {}) =>
        ({ chain }) =>
          {
            const parsedFontSize = options?.fontSize !== null && options?.fontSize !== undefined
              ? parseFloat(String(options.fontSize))
              : NaN;

            return chain()
              .insertContent({
                type: this.name,
                attrs: {
                  latex: stripAlignedEnvironment(latex),
                  fontSize: Number.isFinite(parsedFontSize) ? parsedFontSize : null,
                },
              })
              .insertContent(' ')
              .run();
          },

      updateMathInline:
        (latex, options = {}) =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const selectedNode = selection?.node;

          if (!selectedNode || selectedNode.type.name !== this.name) {
            return false;
          }

          const parsedFontSize = options?.fontSize !== null && options?.fontSize !== undefined
            ? parseFloat(String(options.fontSize))
            : NaN;

          dispatch(
            state.tr.setNodeMarkup(selection.from, undefined, {
              ...selectedNode.attrs,
              latex: stripAlignedEnvironment(latex),
              fontSize: Number.isFinite(parsedFontSize)
                ? parsedFontSize
                : selectedNode.attrs?.fontSize ?? null,
            }),
          );

          return true;
        },
    };
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span');
      dom.setAttribute('data-type', 'math-inline');
      dom.className = 'math-inline-node';
      dom.setAttribute('contenteditable', 'false');

      const latex = String(node.attrs?.latex || '').trim();
      const isDisplay = Boolean(node.attrs?.display);
      const confidenceScore = node.attrs?.confidence_score;
      const parsedFontSize = node.attrs?.fontSize !== null && node.attrs?.fontSize !== undefined
        ? parseFloat(String(node.attrs.fontSize))
        : NaN;
      const customFontSize = Number.isFinite(parsedFontSize) ? parsedFontSize : null;
      const inlineSizePx = customFontSize ? Math.max(12, customFontSize) : null;
      const displaySizePx = inlineSizePx ? inlineSizePx * 1.6 : null;

      if (inlineSizePx) {
        dom.style.setProperty('--math-node-size', `${inlineSizePx}px`);
      }
      if (displaySizePx) {
        dom.style.setProperty('--math-node-display-size', `${displaySizePx}px`);
      }
      
      // Debug: log confidence scores to check if they're being received
      if (isDisplay) {
        console.log(`Math node (display) with latex: "${latex.substring(0, 50)}..." has confidence_score: ${confidenceScore}`);
      }
      
      const renderTarget = document.createElement('span');
      renderTarget.className = 'math-render';

      try {
        // Keep interpretation identical to formula toolbar preview.
        katex.render(latex || '\\text{ }', renderTarget, {
          throwOnError: false,
          displayMode: false,
        });
        if (renderTarget.querySelector('.katex-error') && /\\tag\s*(?:\{[^}]*\}|\d+)/.test(latex)) {
          renderTarget.innerHTML = '';
          const strippedLatex = latex.replace(/\\tag\s*(?:\{[^}]*\}|\d+)/g, '').trim();
          katex.render(strippedLatex || '\\text{ }', renderTarget, {
            throwOnError: false,
            displayMode: false,
          });
        }
      } catch {
        renderTarget.textContent = `$${latex}$`;
      }

      if (isDisplay) {
        dom.classList.add('math-display-node');
      }

      // Apply confidence score styling
      if (typeof confidenceScore === 'number') {
        if (confidenceScore < 0.6) {
          dom.classList.add('math-low-confidence');
        } else if (confidenceScore < 0.85) {
          dom.classList.add('math-medium-confidence');
        }
      }

      dom.appendChild(renderTarget);
      return { dom };
    };
  },
});

// ─── Styles globaux injectés une seule fois ────────────────────────────────────
const EDITOR_STYLES = `
  /* Zone d'édition TipTap */
  .course-editor-content .ProseMirror {
    outline: none;
    min-height: 320px;
    padding: 22px 28px;
    color: var(--ce-text-primary);
    font-family: var(--ce-font-normal);
    font-size: 16.25px;
    font-weight: 460;
    line-height: 1.78;
    letter-spacing: 0.003em;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    font-kerning: normal;
    font-feature-settings: 'liga' 1, 'kern' 1, 'calt' 1;
    caret-color: var(--ce-accent);
    box-sizing: border-box;
    width: 100%;
    max-width: var(--ce-content-max-width, 74ch);
    margin: 0 auto;
  }

  .course-editor-content .ProseMirror p {
    margin: 0 0 0.92em 0;
    font-family: var(--ce-font-normal);
    font-size: 1em;
    font-weight: 460;
    line-height: 1.78;
    letter-spacing: 0.003em;
  }

  .course-editor-content .ProseMirror h1,
  .course-editor-content .ProseMirror h2,
  .course-editor-content .ProseMirror h3,
  .course-editor-content .ProseMirror h4,
  .course-editor-content .ProseMirror h5,
  .course-editor-content .ProseMirror h6 {
    color: var(--ce-text-secondary);
    font-weight: 700;
    font-family: var(--ce-font-ui);
    margin-top: 20px;
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .course-editor-content .ProseMirror h1 { font-size: 26px; }
  .course-editor-content .ProseMirror h2 { font-size: 22px; }
  .course-editor-content .ProseMirror h3 { font-size: 19px; }
  .course-editor-content .ProseMirror h4 { font-size: 17px; }
  .course-editor-content .ProseMirror h5 { font-size: 15px; }
  .course-editor-content .ProseMirror h6 { font-size: 13px; color: var(--ce-text-tertiary); }

  .course-editor-content .ProseMirror strong { color: var(--ce-text-primary); }
  .course-editor-content .ProseMirror em     { color: var(--ce-accent-soft); }
  .course-editor-content .ProseMirror u      { text-decoration-color: var(--ce-accent); }

  /* Listes */
  .course-editor-content .ProseMirror ul,
  .course-editor-content .ProseMirror ol {
    padding-left: 22px;
    margin: 8px 0;
  }
  .course-editor-content .ProseMirror li { margin-bottom: 4px; }
  .course-editor-content .ProseMirror li {
    font-family: var(--ce-font-normal);
    font-size: 0.99em;
    line-height: 1.7;
  }

  /* Placeholder */
  .course-editor-content .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: var(--ce-text-tertiary);
    pointer-events: none;
    float: left;
    height: 0;
  }

  /* Sélection */
  .course-editor-content .ProseMirror ::selection {
    background: var(--ce-accent-selection);
  }

  /* Tableaux */
  .course-editor-content .ProseMirror table {
    border-collapse: collapse;
    width: 100%;
    margin: 12px 0;
    table-layout: fixed;
  }
  .course-editor-content .ProseMirror table td,
  .course-editor-content .ProseMirror table th {
    border: 1px solid var(--ce-border-muted);
    padding: 8px 12px;
    vertical-align: top;
    min-width: 80px;
    color: var(--ce-text-secondary);
  }
  .course-editor-content .ProseMirror table th {
    background: var(--ce-bg-tertiary);
    font-weight: 700;
    color: var(--ce-accent-soft);
  }
  .course-editor-content .ProseMirror table tr:nth-child(even) td {
    background: var(--ce-row-even-bg);
  }

  /* ── Classes de confiance pour les formules mathématiques ─────────────────
     - formula-high   : score >= 0.8   → vert (confiance élevée)
     - formula-medium : score >= 0.5   → orange (confiance moyenne)
     - formula-low    : score < 0.5    → rouge (confiance basse)
     Ces classes sont appliquées par le backend dans le HTML initial.
  */
  .course-editor-content .ProseMirror .formula-high {
    background: var(--ce-success-bg);
    border: 1px solid var(--ce-success-border);
    border-radius: 4px;
    padding: 1px 5px;
    color: var(--ce-success-text);
    font-family: 'Courier New', monospace;
    font-size: 0.93em;
    cursor: default;
  }
  .course-editor-content .ProseMirror .formula-medium {
    background: var(--ce-warning-bg);
    border: 1px solid var(--ce-warning-border);
    border-radius: 4px;
    padding: 1px 5px;
    color: var(--ce-warning-text);
    font-family: 'Courier New', monospace;
    font-size: 0.93em;
    cursor: default;
  }
  .course-editor-content .ProseMirror .formula-low {
    background: var(--ce-error-bg);
    border: 1px solid var(--ce-error-border);
    border-radius: 4px;
    padding: 1px 5px;
    color: var(--ce-error-text);
    font-family: 'Courier New', monospace;
    font-size: 0.93em;
    cursor: default;
  }

  /* Formules inline génériques $...$ */
  .course-editor-content .ProseMirror .math-inline {
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 1px 5px;
    color: var(--ce-text-primary);
    font-family: 'Courier New', monospace;
    font-size: 0.93em;
    box-shadow: none;
    transition: background-color 0.22s ease, border-color 0.22s ease, opacity 0.22s ease;
  }

  .course-editor-content .ProseMirror .math-inline:hover {
    background: var(--ce-accent-hover-bg);
    border-color: var(--ce-accent-hover-border);
  }

  .course-editor-content .ProseMirror .math-inline-node {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-gutter: stable both-edges;
    scrollbar-width: thin;
    scrollbar-color: var(--ce-formula-scroll-thumb-idle) transparent;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 2px 6px;
    margin: 0 2px;
    vertical-align: middle;
    color: var(--ce-text-primary);
    box-shadow: none;
    transition: background-color 0.22s ease, border-color 0.22s ease, opacity 0.22s ease, transform 0.22s ease;
  }

  .course-editor-content .ProseMirror .math-inline-node::-webkit-scrollbar {
    height: 12px;
    width: 12px;
  }

  .course-editor-content .ProseMirror .math-inline-node::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 999px;
  }

  .course-editor-content .ProseMirror .math-inline-node::-webkit-scrollbar-thumb {
    background: var(--ce-formula-scroll-thumb-idle);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
    transition: background 0.15s ease;
  }

  .course-editor-content .ProseMirror .math-inline-node:hover::-webkit-scrollbar-thumb {
    background: var(--ce-formula-scroll-thumb-active);
    background-clip: content-box;
  }

  .course-editor-content .ProseMirror .math-inline-node .math-render {
    display: inline-block;
    max-width: 100%;
  }

  .course-editor-content .ProseMirror .math-inline-node:hover {
    background: var(--ce-accent-hover-bg-strong);
    border-color: var(--ce-accent-hover-border-strong);
  }

  /* Confidence score styling for math nodes */
  .course-editor-content .ProseMirror .math-inline-node.math-medium-confidence {
    background: var(--ce-warning-bg-strong);
    border: 2px solid var(--ce-warning-border-strong);
  }

  .course-editor-content .ProseMirror .math-inline-node.math-low-confidence {
    background: var(--ce-error-bg-strong);
    border: 2px solid var(--ce-error-border-strong);
  }

  .course-editor-content .ProseMirror .math-inline-node .katex {
    font-size: var(--math-node-size, 1.25em);
    color: var(--ce-text-primary);
    text-shadow: none;
    max-width: 100%;
    white-space: normal;
  }

  /* Formule occupant une ligne complete: rendu display + taille x2 */
  .course-editor-content .ProseMirror .math-inline-node.math-display-node {
    display: flex;
    width: 100%;
    justify-content: center;
    margin: 14px 0;
    padding: 8px 10px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-gutter: stable both-edges;
    scrollbar-width: thin;
    scrollbar-color: var(--ce-formula-scroll-thumb-idle) transparent;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node::-webkit-scrollbar {
    height: 12px;
    width: 12px;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 999px;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node::-webkit-scrollbar-thumb {
    background: var(--ce-formula-scroll-thumb-idle);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
    transition: background 0.15s ease;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node:hover::-webkit-scrollbar-thumb {
    background: var(--ce-formula-scroll-thumb-active);
    background-clip: content-box;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node .katex {
    font-size: var(--math-node-display-size, 2em);
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node .math-render {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    max-width: 100%;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node .math-render-line {
    display: block;
    max-width: 100%;
    text-align: center;
    white-space: nowrap;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node.math-display-compact .katex {
    font-size: 1.75em;
  }

  .course-editor-content .ProseMirror .math-inline-node.math-display-node.math-display-super-compact .katex {
    font-size: 1.55em;
  }

  /* Scrollbar native du conteneur éditeur masquée (on garde la scrollbar React custom) */
  .course-editor-content {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .course-editor-content::-webkit-scrollbar {
    display: none;
  }

  /* Images insérées dans l'éditeur */
  .course-editor-content .ProseMirror img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    display: block;
    margin: 8px 0;
    border: 2px solid transparent;
    cursor: pointer;
    transition: border-color 0.15s;
  }
  .course-editor-content .ProseMirror img.ProseMirror-selectednode {
    border-color: var(--ce-accent);
    outline: none;
  }

  @keyframes textSelectionToolbarIn {
    from {
      opacity: 0;
      filter: blur(1.5px);
    }
    to {
      opacity: 1;
      filter: blur(0);
    }
  }
`;

// ─── Injection des styles une seule fois ──────────────────────────────────────
let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected) return;
  stylesInjected = true;
  const tag = document.createElement('style');
  tag.textContent = EDITOR_STYLES;
  document.head.appendChild(tag);
};

// ─── CourseEditor ─────────────────────────────────────────────────────────────
/**
 * Éditeur de cours riche basé sur TipTap avec styles personnalisables.
 *
 * @param {object}   initialContent        Contenu JSON TipTap initial
 * @param {function} onChange              Callback pour mise à jour contenu
 * @param {boolean}  readOnly              Mode lecture seule
 * @param {string}   placeholder           Texte placeholder
 * @param {string}   editorBgColor         Couleur fond éditeur (ex: '#1A1A1A')
 * @param {string}   editorTextColor       Couleur texte éditeur (ex: '#F5F5F5')
 * @param {string}   toolbarBgColor        Couleur fond toolbar (ex: '#252525')
 * @param {string}   editorHeight          Hauteur éditeur (ex: '400px', '60vh')
 * @param {string}   toolbarHeight         Hauteur toolbar (ex: '50px', '4rem')
 * @param {string}   editorWidth           Largeur éditeur (ex: '100%', '800px')
 * @param {string}   editorContentMaxWidth Largeur max du texte (ex: '74ch', '140ch', '100%')
 * @param {object}   containerStyle        Styles additionnels wrapper
 * @param {object}   toolbarStyle          Styles additionnels toolbar
 */
const CourseEditor = forwardRef(({
  initialContent = EMPTY_TIPTAP_DOC,
  onChange,
  onManualSync,
  readOnly = false,
  placeholder = 'Start writing your course here...',
  editorBgColor,
  editorTextColor,
  toolbarBgColor,
  editorHeight = '60vh',
  toolbarHeight = '50px',
  editorWidth = '100%',
  editorContentMaxWidth = '74ch',
  toolbarWidth = '100%',
  containerStyle,
  toolbarStyle,
}, ref) => {
  const isValidDocPos = (doc, pos) => Number.isInteger(pos) && pos >= 0 && pos < doc.content.size;

  const {
    bgPrimary,
    bgSecondary,
    bgTertiary,
    textPrimary,
    textSecondary,
    textTertiary,
    activeAccent,
    functionalColors,
  } = useColorContext();

  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0A');
  const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
  const safeBgTertiary = toSafeHex(bgTertiary, '#1E1E1E');
  const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
  const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
  const safeTextTertiary = safeTextPrimary;
  const safeAccent = toSafeHex(activeAccent, '#3B82F6');
  const miniToolbarBg = hexToRgba(safeBgTertiary, 0.94, safeBgTertiary);
  const safeSuccess = toSafeHex(functionalColors?.success, safeAccent);
  const safeWarning = toSafeHex(functionalColors?.warning, safeAccent);
  const safeError = toSafeHex(functionalColors?.error, safeAccent);

  const resolvedEditorBg = editorBgColor || safeBgPrimary;
  const resolvedEditorText = editorTextColor || safeTextPrimary;
  const resolvedToolbarBg = toolbarBgColor || hexToRgba(safeBgTertiary, 0.95, safeBgTertiary);

  injectStyles();
  const normalizedInitialContent = normalizeEditorIncomingContent(initialContent);
  const skipNextExternalSyncRef = useRef(false);
  const pendingDocRef = useRef(null);
  const onChangeTimerRef = useRef(null);
  const [imageContextMenu, setImageContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    pos: null,
    src: '',
  });
  const [hoveredImageAction, setHoveredImageAction] = useState('');
  const [selectedImagePos, setSelectedImagePos] = useState(null);
  const [selectedImageRect, setSelectedImageRect] = useState(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState('');
  const [textSelectionMenu, setTextSelectionMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  const [hoveredTextAction, setHoveredTextAction] = useState('');
  const isSelectingTextRef = useRef(false);
    const editorRef = useRef(null);
  const resizeImageRef = useRef({
    active: false,
    startX: 0,
    startWidth: 0,
    pos: null,
    maxWidth: 1200,
  });

  const closeImageContextMenu = useCallback(() => {
    setImageContextMenu((prev) => (prev.visible ? { ...prev, visible: false, pos: null, src: '' } : prev));
    setHoveredImageAction('');
  }, []);

  const resolveImagePos = useCallback((view, imgElement) => {
    if (!view || !(imgElement instanceof HTMLImageElement)) return null;
    const basePos = view.posAtDOM(imgElement, 0);
    const candidates = [basePos, basePos - 1, basePos + 1].filter((pos) => isValidDocPos(view.state.doc, pos));

    for (const pos of candidates) {
      const node = view.state.doc.nodeAt(pos);
      if (node?.type?.name === 'image') return pos;
    }

    return null;
  }, []);

  const resolveMathInlinePos = useCallback((view, mathElement) => {
    if (!view || !(mathElement instanceof HTMLElement)) return null;
    const basePos = view.posAtDOM(mathElement, 0);
    const candidates = [basePos, basePos - 1, basePos + 1, basePos - 2, basePos + 2]
      .filter((pos) => isValidDocPos(view.state.doc, pos));

    for (const pos of candidates) {
      const node = view.state.doc.nodeAt(pos);
      if (node?.type?.name === 'mathInline') return pos;
    }

    return null;
  }, []);

  const resolveImageNodeRef = useCallback((state, pos, src = '') => {
    if (!state || typeof pos !== 'number') return null;

    for (let offset = -4; offset <= 4; offset += 1) {
      const candidatePos = pos + offset;
      if (!isValidDocPos(state.doc, candidatePos)) continue;
      const node = state.doc.nodeAt(candidatePos);
      if (node?.type?.name === 'image') {
        if (!src || node.attrs?.src === src) return { node, pos: candidatePos };
      }
    }

    if (src) {
      let best = null;
      state.doc.descendants((node, nodePos) => {
        if (node.type?.name !== 'image') return;
        if (node.attrs?.src !== src) return;
        const dist = Math.abs(nodePos - pos);
        if (!best || dist < best.dist) best = { node, pos: nodePos, dist };
      });
      if (best) return { node: best.node, pos: best.pos };
    }

    return null;
  }, []);

  const flushPendingChange = useCallback(() => {
    if (!onChange) return;

    const doc = pendingDocRef.current || (editorRef.current ? editorRef.current.getJSON() : null);
    if (!doc) return;

    pendingDocRef.current = null;
    onChange(doc);
  }, [onChange]);

  const triggerManualSync = useCallback(() => {
    const doc = pendingDocRef.current || (editorRef.current ? editorRef.current.getJSON() : null);
    if (!doc) return;

    if (onChange) {
      pendingDocRef.current = null;
      onChange(doc);
    }

    if (onManualSync) {
      onManualSync(doc);
    }
  }, [onChange, onManualSync]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        underline: true, // Explicitly enable underline from StarterKit
      }),
      MathInline,
      TextStyle,
      FontSize,
      FontFamily,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      ResizableImage.configure({ inline: false, allowBase64: true }),
    ],
    content: normalizedInitialContent,
    editable: !readOnly,
    editorProps: {
      attributes: {
        'data-placeholder': placeholder,
        spellCheck: false,
        autoCorrect: 'off',
        autoCapitalize: 'off',
        lang: 'en-US',
      },
      clipboardTextSerializer: (slice) => serializeFragmentToClipboardText(slice.content),
      handleDOMEvents: {
        copy: (view, event) => {
          if (!event?.clipboardData) return false;
          const { selection } = view.state;
          if (selection.empty) return false;

          const text = serializeFragmentToClipboardText(selection.content().content);
          event.clipboardData.setData('text/plain', text);
          event.preventDefault();
          return true;
        },
        mousedown: (view, event) => {
          isSelectingTextRef.current = true;
          const target = event.target;
          if (!(target instanceof HTMLElement)) return false;

          const mathNode = target.closest('[data-type="math-inline"]');
          if (mathNode && view.dom.contains(mathNode)) {
            const pos = resolveMathInlinePos(view, mathNode);
            if (typeof pos === 'number') {
              const tr = view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos));
              view.dispatch(tr);
              view.focus();
              event.preventDefault();
              return true;
            }
          }

          const img = target.closest('img');
          if (!img || !view.dom.contains(img)) {
            setSelectedImagePos(null);
            setSelectedImageRect(null);
            setSelectedImageSrc('');
            return false;
          }

          const pos = resolveImagePos(view, img);
          if (typeof pos !== 'number') return false;
          setSelectedImagePos(pos);
          setSelectedImageRect(img.getBoundingClientRect());
          setSelectedImageSrc(String(img.getAttribute('src') || ''));
          return false;
        },
        contextmenu: (view, event) => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) {
            closeImageContextMenu();
            return false;
          }

          const img = target.closest('img');
          if (!img || !view.dom.contains(img)) {
            closeImageContextMenu();
            return false;
          }

          event.preventDefault();
          const pos = resolveImagePos(view, img);
          if (typeof pos !== 'number') return true;
          const src = String(img.getAttribute('src') || '');
          setSelectedImagePos(pos);
          setSelectedImageRect(img.getBoundingClientRect());
          setSelectedImageSrc(src);
          setImageContextMenu({
            visible: true,
            x: event.clientX,
            y: event.clientY,
            pos,
            src,
          });
          return true;
        },
      },
    },
    onUpdate: ({ editor: e }) => {
      skipNextExternalSyncRef.current = true;
      pendingDocRef.current = e.getJSON();

      if (onChangeTimerRef.current) {
        clearTimeout(onChangeTimerRef.current);
        onChangeTimerRef.current = null;
      }

      flushPendingChange();
    },
    onBlur: () => {
      if (onChangeTimerRef.current) {
        clearTimeout(onChangeTimerRef.current);
        onChangeTimerRef.current = null;
      }
      flushPendingChange();
    },
  });

  useEffect(() => {
    editorRef.current = editor || null;
  }, [editor]);

  useImperativeHandle(ref, () => ({
    flushChanges: flushPendingChange,
    getCurrentContent: () => pendingDocRef.current || (editorRef.current ? editorRef.current.getJSON() : null),
    manualSync: triggerManualSync,
  }), [flushPendingChange, triggerManualSync]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      triggerManualSync();
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [triggerManualSync]);

  // Synchroniser le contenu si il change depuis l'extérieur
  useEffect(() => {
    if (!editor) return;

    const normalizedContent = normalizeEditorIncomingContent(initialContent);
    const safeContent = normalizedContent || EMPTY_TIPTAP_DOC;

    if (skipNextExternalSyncRef.current) {
      const isSameEcho = areContentsEquivalent(normalizedContent, pendingDocRef.current);
      skipNextExternalSyncRef.current = false;
      if (isSameEcho) {
        return;
      }
    }

    if (isTipTapDoc(normalizedContent)) {
      try {
        const incomingDocNode = editor.schema.nodeFromJSON(normalizedContent);
        if (incomingDocNode.eq(editor.state.doc)) return;
      } catch {
        // Ignore malformed incoming docs; setContent below will sanitize through TipTap.
      }
    }

    editor.commands.setContent(safeContent, false);
  }, [initialContent, editor]);

  useEffect(() => () => {
    if (onChangeTimerRef.current) {
      clearTimeout(onChangeTimerRef.current);
      onChangeTimerRef.current = null;
    }
  }, []);

  // Mettre à jour editable si readOnly change
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  // ── Scrollbar indépendante ──────────────────────────────────────────────────
  const contentRef   = useRef(null);
  const isDragging   = useRef(false);
  const dragStartY   = useRef(0);
  const dragStartTop = useRef(0);
  const resizeRafRef = useRef(0);
  const thumbMetricsRef = useRef({ height: 40, top: 0 });
  const [thumbHeight, setThumbHeight] = useState(40);
  const [thumbTop,    setThumbTop]    = useState(0);
  const SCROLLBAR_W = 14;

  const updateThumb = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const ratio  = el.clientHeight / el.scrollHeight;
    const trackH = el.clientHeight;
    const newH   = Math.max(28, ratio * trackH);
    const maxTop = trackH - newH;
    const newTop = ratio >= 1 ? 0 : (el.scrollTop / (el.scrollHeight - el.clientHeight)) * maxTop;

    const prev = thumbMetricsRef.current;
    if (Math.abs(prev.height - newH) < 0.5 && Math.abs(prev.top - newTop) < 0.5) return;

    thumbMetricsRef.current = { height: newH, top: newTop };
    setThumbHeight(newH);
    setThumbTop(newTop);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateThumb);

    const scheduleThumbUpdate = () => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = 0;
        updateThumb();
      });
    };

    const ro = new ResizeObserver(scheduleThumbUpdate);
    ro.observe(el);
    updateThumb();

    return () => {
      el.removeEventListener('scroll', updateThumb);
      ro.disconnect();
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = 0;
      }
    };
  }, [updateThumb]);

  // Gérer le collage d'images depuis le presse-papiers
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !editor) return;
    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          const reader = new FileReader();
          reader.onload = (e) => {
            const src = e.target?.result;
            if (src) editor.chain().focus().setImage({ src }).run();
          };
          reader.readAsDataURL(file);
          break;
        }
      }
    };
    el.addEventListener('paste', handlePaste);
    return () => el.removeEventListener('paste', handlePaste);
  }, [editor]);

  useEffect(() => {
    if (!imageContextMenu.visible) return undefined;

    const handlePointerDown = () => closeImageContextMenu();
    const handleEscape = (event) => {
      if (event.key === 'Escape') closeImageContextMenu();
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('scroll', handlePointerDown, true);
    window.addEventListener('resize', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('scroll', handlePointerDown, true);
      window.removeEventListener('resize', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [imageContextMenu.visible, closeImageContextMenu]);

  const updateTextSelectionMenu = useCallback(() => {
    if (!editor || readOnly) {
      setTextSelectionMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }

    if (isSelectingTextRef.current) {
      setTextSelectionMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }

    const { from, to, empty } = editor.state.selection;
    if (empty || from === to) {
      setTextSelectionMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }

    let hasMathInlineSelection = false;
    if (editor.state.selection?.node?.type?.name === 'mathInline') {
      hasMathInlineSelection = true;
    } else {
      editor.state.doc.nodesBetween(from, to, (node) => {
        if (node?.type?.name === 'mathInline') {
          hasMathInlineSelection = true;
          return false;
        }
        return true;
      });
    }

    if (hasMathInlineSelection) {
      setTextSelectionMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }

    const start = editor.view.coordsAtPos(from);
    const end = editor.view.coordsAtPos(to);
    const centerX = (start.left + end.right) / 2;
    const y = Math.min(start.top, end.top) - 14;

    setTextSelectionMenu({
      visible: true,
      x: centerX,
      y,
    });
  }, [editor, readOnly]);

  useEffect(() => {
    const onPointerUp = () => {
      if (isSelectingTextRef.current) {
        isSelectingTextRef.current = false;
        updateTextSelectionMenu();
      }
    };

    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchend', onPointerUp);
    return () => {
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchend', onPointerUp);
    };
  }, [updateTextSelectionMenu]);

  useEffect(() => {
    if (!editor || readOnly) return undefined;

    const sync = () => updateTextSelectionMenu();
    sync();

    editor.on('selectionUpdate', sync);
    editor.on('update', sync);
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);

    return () => {
      editor.off('selectionUpdate', sync);
      editor.off('update', sync);
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [editor, readOnly, updateTextSelectionMenu]);

  const applyImageAttrsAtPos = useCallback((pos, updater, src = '') => {
    if (!editor || typeof pos !== 'number') return;
    const { state, view } = editor;
    const resolved = resolveImageNodeRef(state, pos, src);
    if (!resolved?.node || resolved.node.type.name !== 'image') return;
    const { node, pos: nodePos } = resolved;

    const nextAttrs = typeof updater === 'function' ? updater(node.attrs || {}) : updater;
    const tr = state.tr.setNodeMarkup(nodePos, undefined, {
      ...(node.attrs || {}),
      ...(nextAttrs || {}),
    });

    view.dispatch(tr);
    editor.commands.focus();
  }, [editor, resolveImageNodeRef]);

  const setImageWidthFromMenu = useCallback((width) => {
    if (typeof imageContextMenu.pos !== 'number') return;
    applyImageAttrsAtPos(imageContextMenu.pos, { width }, imageContextMenu.src || selectedImageSrc);
    closeImageContextMenu();
  }, [imageContextMenu.pos, imageContextMenu.src, selectedImageSrc, applyImageAttrsAtPos, closeImageContextMenu]);

  const setImageAlignFromMenu = useCallback((align) => {
    if (typeof imageContextMenu.pos !== 'number') return;
    applyImageAttrsAtPos(imageContextMenu.pos, { align }, imageContextMenu.src || selectedImageSrc);
    closeImageContextMenu();
  }, [imageContextMenu.pos, imageContextMenu.src, selectedImageSrc, applyImageAttrsAtPos, closeImageContextMenu]);

  const deleteImageFromMenu = useCallback(() => {
    if (!editor || typeof imageContextMenu.pos !== 'number') return;
    const { state, view } = editor;
    const resolved = resolveImageNodeRef(state, imageContextMenu.pos, imageContextMenu.src || selectedImageSrc);
    const node = resolved?.node;
    const nodePos = resolved?.pos;
    if (!node || node.type.name !== 'image') {
      closeImageContextMenu();
      return;
    }

    const tr = state.tr.delete(nodePos, nodePos + node.nodeSize);
    view.dispatch(tr);
    editor.commands.focus();
    closeImageContextMenu();
  }, [editor, imageContextMenu.pos, imageContextMenu.src, selectedImageSrc, closeImageContextMenu, resolveImageNodeRef]);

  const updateSelectedImageRect = useCallback((pos) => {
    if (!editor || typeof pos !== 'number') {
      setSelectedImageRect(null);
      return;
    }
    const dom = editor.view.nodeDOM(pos);
    if (!(dom instanceof HTMLImageElement)) {
      setSelectedImageRect(null);
      return;
    }
    setSelectedImageRect(dom.getBoundingClientRect());
  }, [editor]);

  useEffect(() => {
    if (!editor || typeof selectedImagePos !== 'number') return undefined;
    const sync = () => updateSelectedImageRect(selectedImagePos);

    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);

    editor.on('update', sync);
    editor.on('selectionUpdate', sync);

    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
      editor.off('update', sync);
      editor.off('selectionUpdate', sync);
    };
  }, [editor, selectedImagePos, updateSelectedImageRect]);

  const startImageResize = useCallback((event) => {
    if (!editor || typeof selectedImagePos !== 'number') return;
    event.preventDefault();
    event.stopPropagation();

    const dom = editor.view.nodeDOM(selectedImagePos);
    if (!(dom instanceof HTMLImageElement)) return;

    const startWidth = dom.getBoundingClientRect().width;
    const maxWidth = contentRef.current?.getBoundingClientRect().width || 1200;

    resizeImageRef.current = {
      active: true,
      startX: event.clientX,
      startWidth,
      pos: selectedImagePos,
      maxWidth,
      src: selectedImageSrc,
    };

    const onMove = (moveEvent) => {
      if (!resizeImageRef.current.active) return;
      const delta = moveEvent.clientX - resizeImageRef.current.startX;
      const raw = resizeImageRef.current.startWidth + delta;
      const next = Math.min(resizeImageRef.current.maxWidth, Math.max(80, raw));
      dom.style.width = `${Math.round(next)}px`;
      dom.style.height = 'auto';
      updateSelectedImageRect(resizeImageRef.current.pos);
    };

    const onUp = () => {
      if (resizeImageRef.current.active) {
        const finalWidth = Math.round(dom.getBoundingClientRect().width);
        applyImageAttrsAtPos(
          resizeImageRef.current.pos,
          { width: `${finalWidth}px` },
          resizeImageRef.current.src || selectedImageSrc,
        );
      }
      resizeImageRef.current.active = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [editor, selectedImagePos, selectedImageSrc, applyImageAttrsAtPos, updateSelectedImageRect]);

  const imageMenuActions = [
    {
      key: 'delete',
      label: 'Delete',
      icon: <FiTrash2 size={13} />,
      onClick: deleteImageFromMenu,
      danger: true,
    },
    {
      key: 'align-left',
      label: 'Align left',
      icon: <MdFormatAlignLeft size={14} />,
      onClick: () => setImageAlignFromMenu('left'),
    },
    {
      key: 'align-center',
      label: 'Center',
      icon: <MdFormatAlignCenter size={14} />,
      onClick: () => setImageAlignFromMenu('center'),
    },
    {
      key: 'align-right',
      label: 'Align right',
      icon: <MdFormatAlignRight size={14} />,
      onClick: () => setImageAlignFromMenu('right'),
    },
  ];

  const textQuickActions = [
    {
      key: 'bold',
      label: 'Gras',
      icon: <FiBold size={13} />,
      active: editor.isActive('bold'),
      onClick: () => editor.chain().toggleBold().run(),
    },
    {
      key: 'italic',
      label: 'Italique',
      icon: <FiItalic size={13} />,
      active: editor.isActive('italic'),
      onClick: () => editor.chain().toggleItalic().run(),
    },
    {
      key: 'underline',
      label: 'Souligne',
      icon: <FiUnderline size={13} />,
      active: editor.isActive('underline'),
      onClick: () => editor.chain().toggleUnderline().run(),
    },
  ];

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    isDragging.current   = true;
    dragStartY.current   = e.clientY;
    dragStartTop.current = thumbTop;
    const onMove = (ev) => {
      if (!isDragging.current) return;
      const el = contentRef.current;
      if (!el) return;
      const trackH = el.clientHeight;
      const maxTop = trackH - thumbHeight;
      const newTop = Math.min(maxTop, Math.max(0, dragStartTop.current + (ev.clientY - dragStartY.current)));
      el.scrollTop = (newTop / maxTop) * (el.scrollHeight - el.clientHeight);
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const showScrollbar = thumbHeight < (contentRef.current?.clientHeight ?? 1);

  return (
    <div
      style={{
        background: resolvedEditorBg,
        border: `1px solid ${hexToRgba(safeTextSecondary, 0.28, safeTextSecondary)}`,
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: editorWidth,
        alignItems: 'center',
        '--ce-text-primary': resolvedEditorText,
        '--ce-text-secondary': resolvedEditorText,
        '--ce-text-tertiary': resolvedEditorText,
        '--ce-font-normal': `'Inter', 'Segoe UI', 'SF Pro Text', system-ui, sans-serif`,
        '--ce-font-ui': `'Inter', 'Segoe UI', 'SF Pro Display', system-ui, sans-serif`,
        '--ce-accent': safeAccent,
        '--ce-accent-soft': hexToRgba(safeAccent, 0.85, safeAccent),
        '--ce-accent-selection': hexToRgba(safeAccent, 0.35, safeAccent),
        '--ce-accent-hover-bg': hexToRgba(safeAccent, 0.14, safeAccent),
        '--ce-accent-hover-border': hexToRgba(safeAccent, 0.35, safeAccent),
        '--ce-accent-hover-bg-strong': hexToRgba(safeAccent, 0.16, safeAccent),
        '--ce-accent-hover-border-strong': hexToRgba(safeAccent, 0.4, safeAccent),
        '--ce-border-muted': hexToRgba(safeTextSecondary, 0.3, safeTextSecondary),
        '--ce-bg-tertiary': safeBgTertiary,
        '--ce-row-even-bg': hexToRgba(safeTextPrimary, 0.02, safeTextPrimary),
        '--ce-success-bg': hexToRgba(safeSuccess, 0.12, safeSuccess),
        '--ce-success-border': hexToRgba(safeSuccess, 0.4, safeSuccess),
        '--ce-success-text': hexToRgba(safeSuccess, 0.78, safeSuccess),
        '--ce-warning-bg': hexToRgba(safeWarning, 0.12, safeWarning),
        '--ce-warning-border': hexToRgba(safeWarning, 0.4, safeWarning),
        '--ce-warning-text': hexToRgba(safeWarning, 0.8, safeWarning),
        '--ce-error-bg': hexToRgba(safeError, 0.12, safeError),
        '--ce-error-border': hexToRgba(safeError, 0.4, safeError),
        '--ce-error-text': hexToRgba(safeError, 0.8, safeError),
        '--ce-warning-bg-strong': hexToRgba(safeWarning, 0.18, safeWarning),
        '--ce-warning-border-strong': hexToRgba(safeWarning, 0.6, safeWarning),
        '--ce-error-bg-strong': hexToRgba(safeError, 0.18, safeError),
        '--ce-error-border-strong': hexToRgba(safeError, 0.7, safeError),
        '--ce-formula-scroll-thumb-idle': hexToRgba(safeAccent, 0.6, safeAccent),
        '--ce-formula-scroll-thumb-active': hexToRgba(safeAccent, 0.85, safeAccent),
        '--ce-content-max-width': editorContentMaxWidth,
        ...containerStyle,
      }}
    >
      {/* Barre d'outils — masquée en lecture seule */}
      {!readOnly && (
        <TextEditorToolsBar
          editor={editor}
          bgColor={resolvedToolbarBg}
          height={toolbarHeight}
          toolbarWidth={toolbarWidth}
          style={toolbarStyle}
        />
      )}

      {/* Zone contenu + scrollbar indépendante */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: editorHeight,
          minHeight: editorHeight,
          maxHeight: editorHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Contenu éditable — scrollbar native masquée */}
        <div
          ref={contentRef}
          className="course-editor-content"
          style={{
            overflowY: 'scroll',
            height: '100%',
            minHeight: '100%',
            maxHeight: '100%',
            background: resolvedEditorBg,
            color: resolvedEditorText,
            width: toolbarWidth,
            boxSizing: 'border-box',
            borderRadius: 0,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <EditorContent editor={editor} />

          {imageContextMenu.visible && (
            <div
              style={{
                position: 'fixed',
                top: imageContextMenu.y,
                left: imageContextMenu.x,
                zIndex: 2600,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                background: miniToolbarBg,
                border: `1px solid ${hexToRgba(safeAccent, 0.46, safeAccent)}`,
                borderRadius: 999,
                padding: '8px 10px',
                boxShadow: 'none',
                backdropFilter: 'blur(10px)',
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              {imageMenuActions.map((action) => {
                const isHovered = hoveredImageAction === action.key;
                return (
                  <button
                    key={action.key}
                    type="button"
                    onClick={action.onClick}
                    onMouseEnter={() => setHoveredImageAction(action.key)}
                    onMouseLeave={() => setHoveredImageAction('')}
                    style={{
                      position: 'relative',
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      border: 'none',
                      background: action.danger
                        ? hexToRgba(safeError, isHovered ? 0.3 : 0.18, safeError)
                        : hexToRgba(safeTextPrimary, isHovered ? 0.14 : 0.1, safeTextPrimary),
                      color: safeTextTertiary,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
                      transition: 'transform 0.16s ease, background 0.16s ease, border-color 0.16s ease, color 0.16s ease',
                      boxShadow: 'none',
                    }}
                  >
                    {action.icon}
                    <span
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: 'calc(100% + 8px)',
                        transform: `translateX(-50%) ${isHovered ? 'translateY(0)' : 'translateY(-4px)'}`,
                        opacity: isHovered ? 1 : 0,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        fontSize: 11,
                        color: safeTextTertiary,
                        background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
                        border: 'none',
                        borderRadius: 999,
                        padding: '4px 8px',
                        boxShadow: 'none',
                        transition: 'opacity 0.16s ease, transform 0.16s ease',
                      }}
                    >
                      {action.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {textSelectionMenu.visible && (
            <div
              style={{
                position: 'fixed',
                left: textSelectionMenu.x,
                top: textSelectionMenu.y,
                transform: 'translate(-50%, -100%)',
                zIndex: 2700,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                background: miniToolbarBg,
                border: `1px solid ${hexToRgba(safeAccent, 0.42, safeAccent)}`,
                borderRadius: 999,
                padding: '6px 8px',
                boxShadow: 'none',
                backdropFilter: 'blur(10px)',
                animation: 'textSelectionToolbarIn .16s ease-out',
              }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => event.stopPropagation()}
            >
              {textQuickActions.map((action) => {
                const isHovered = hoveredTextAction === action.key;
                return (
                  <button
                    key={action.key}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      action.onClick();
                      updateTextSelectionMenu();
                    }}
                    onMouseEnter={() => setHoveredTextAction(action.key)}
                    onMouseLeave={() => setHoveredTextAction('')}
                    style={{
                      position: 'relative',
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      border: 'none',
                      background: action.active
                        ? hexToRgba(safeTextPrimary, 0.14, safeTextPrimary)
                        : hexToRgba(safeTextPrimary, isHovered ? 0.14 : 0.1, safeTextPrimary),
                      color: safeTextTertiary,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
                      transition: 'transform 0.16s ease, background 0.16s ease, border-color 0.16s ease, color 0.16s ease',
                      boxShadow: 'none',
                    }}
                  >
                    {action.icon}
                    <span
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: 'calc(100% + 6px)',
                        transform: `translateX(-50%) ${isHovered ? 'translateY(0)' : 'translateY(-4px)'}`,
                        opacity: isHovered ? 1 : 0,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        fontSize: 10,
                        color: safeTextTertiary,
                        background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
                        border: 'none',
                        borderRadius: 999,
                        padding: '3px 7px',
                        boxShadow: 'none',
                        transition: 'opacity 0.16s ease, transform 0.16s ease',
                      }}
                    >
                      {action.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectedImageRect && (
            <button
              type="button"
              onMouseDown={startImageResize}
              title="Resize image"
              style={{
                position: 'fixed',
                left: selectedImageRect.right - 8,
                top: selectedImageRect.bottom - 8,
                width: 16,
                height: 16,
                borderRadius: 4,
                border: 'none',
                background: hexToRgba(safeAccent, 0.24, safeAccent),
                boxShadow: 'none',
                cursor: 'nwse-resize',
                zIndex: 2590,
                padding: 0,
              }}
            />
          )}
        </div>

        {/* Scrollbar indépendante collée au bord droit de l'éditeur */}
        {showScrollbar && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: SCROLLBAR_W,
              height: '100%',
            }}
          >
            <div
              onMouseDown={onThumbMouseDown}
              style={{
                position: 'absolute',
                top: thumbTop,
                left: 2,
                width: SCROLLBAR_W - 4,
                height: thumbHeight,
                background: hexToRgba(safeAccent, 0.62, safeAccent),
                border: 'none',
                borderRadius: 999,
                cursor: 'grab',
                userSelect: 'none',
                boxShadow: 'none',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = hexToRgba(safeAccent, 0.9, safeAccent);
                e.currentTarget.style.borderColor = safeAccent;
                e.currentTarget.style.boxShadow = 'none';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = hexToRgba(safeAccent, 0.62, safeAccent);
                e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.82, safeAccent);
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
      </div>

    </div>
  );
});

CourseEditor.displayName = 'CourseEditor';

export default CourseEditor;
